import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import { BtnBar, DangerAlert, InfoAlert, Table, WaitingAlert } from '@tmapy/style-guide';

import { FilterBtn } from '../../app/components/FilterBtn';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { QUERY_STATE } from '../../constants';
import { useSelector } from '../../hooks';
import { useServiceOrdersListResource } from '../../openapi/operations/serviceOrdersList';
import { stripEmptyFilters } from '../../utils/queryArgs';

import { ServiceOrderFilter } from './ServiceOrderFilter';
import { ServiceOrderListItem } from './ServiceOrderListItem';
import { serviceOrderBtnMessages, serviceOrderMessages as msg } from './serviceOrderMessages';

export const ServiceOrderList: React.FC = () => {
  const dispatch = useDispatch();
  const navigationType = useNavigationType();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const filters = useSelector((state) => state.filter.serviceOrderList);

  const query = stripEmptyFilters(filters);

  const { queryState, data, fetchMore } = useServiceOrdersListResource(
    query,
    navigationType !== 'PUSH',
  );

  const serviceOrders = data?.results ?? [];
  const serviceOrderCount = data?.count;

  const handleNextBtnClick = useCallback(() => {
    if (data?.next) {
      fetchMore(data.next);
    }
  }, [fetchMore, data]);

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(serviceOrderBtnMessages.filter)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='serviceOrders'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...msg.listPageTitle} />
            </PageTitle>
            <span className='sg-a-f-a' />
            <BtnBar>
              <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
            </BtnBar>
          </header>
          {queryState === QUERY_STATE.WAITING && serviceOrders.length < 1 && (
            <WaitingAlert>
              <FormattedMessage {...msg.stateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && serviceOrders.length < 1 && (
            <DangerAlert>
              <FormattedMessage {...msg.stateFail} />
            </DangerAlert>
          )}
          {serviceOrders.length > 0 ? (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th className='sg-a-ta-c'>
                        <div>
                          <FormattedMessage {...msg.documentNumber} />
                        </div>
                        <div>
                          <FormattedMessage {...msg.state} />
                        </div>
                      </th>
                      <th>
                        <FormattedMessage {...msg.name} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.priceListItem} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.quantity} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.totalGross} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.odberatel} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.created} />
                        <br />
                        <span className='sg-u-c-cta-black'>
                          <FormattedMessage {...msg.paid} />
                        </span>
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {serviceOrders.map((serviceOrder) => (
                      <ServiceOrderListItem key={serviceOrder.id} {...serviceOrder} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={serviceOrders.length}
                total={serviceOrderCount!}
                queryState={queryState}
                onNextBtnClick={handleNextBtnClick}
              />
            </div>
          ) : (
            queryState === QUERY_STATE.SUCCESS && (
              <InfoAlert>
                <FormattedMessage {...(isFiltered ? msg.stateFilteredEmpty : msg.stateEmpty)} />
              </InfoAlert>
            )
          )}
        </article>
      </MainContent>
      <ServiceOrderFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
